<template>
    <div
        :class="[
            'input-wrapper',
            'input-wrapper--field',
            'input-wrapper--' + type,
            'input-wrapper--' + name,
        ]"
        @click="$emit('click')"
    >
        <label :class="[hasError ? 'has-error' : '', isValid ? 'valid' : '']">
            <MaskInput
                :type="type"
                :name="name"
                :disabled="disabled"
                :value="model"
                :mask="modelPattern"
                :placeholder="placeholder"
                :inputmode="isNumeric ? 'decimal' : 'text'"
                @input="onInput"
                @blur="validate()"
                @focus="$emit('keyup')"
            />

            <div
                :class="[
                    model?.length ? 'not-empty' : '',
                    'input__label',
                    'input__label--' + name,
                    'input__label--' + type,
                ]"
            >
                <slot />
            </div>
        </label>
        <Error v-if="handlesError" :name="name" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import Error from '@/components/basic/Error.vue';

export default defineComponent({
    name: 'FieldMask',
    components: {
        Error,
    },
    props: {
        type: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        modelValue: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
        pattern: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        isValidated: {
            type: Boolean,
            default: true,
        },
        isNumeric: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'blur', 'click', 'keyup'],
    setup(props, { emit }) {
        const store = useStore();

        const isValid = ref(false);
        const model = ref(props.modelValue);
        const modelPattern = ref(props.pattern);
        const isDataPreloaded = ref(false);

        const isValidFieldsChanged = computed(
            () => store.state.isValidFieldsChanged
        );
        const formDataStore = computed(() => store.state.formData);
        const hasError = computed(() =>
            Boolean(store.state.errors.get(props.name))
        );

        function onInput(event: Event) {
            const value = (event.target as HTMLInputElement)?.value || '';
            model.value = value;
            emit('update:modelValue', value);
        }

        function loadData(): void {
            const formData = formDataStore.value as Record<string, any>;
            if (
                Object.prototype.hasOwnProperty.call(formData, props.name) &&
                formData[props.name] !== null
            ) {
                isDataPreloaded.value = true;
                model.value = String(formData[props.name]);
                emit('update:modelValue', model.value);
            }
        }

        function validate() {
            if (props.isValidated) {
                store.dispatch('validate', {
                    name: props.name,
                    value: model.value,
                });
            }
            emit('blur', model.value);
        }
        onMounted(() => {
            loadData();
        });

        watch(isValidFieldsChanged, () => {
            isValid.value = store.state.validFields.has(props.name);
        });

        watch(model, (newVal) => {
            if (!isDataPreloaded.value) {
                store.commit('DELETE_ERROR', props.name);
            } else {
                isDataPreloaded.value = false;
            }

            emit('update:modelValue', newVal);
        });

        return {
            isValid,
            model,
            modelPattern,
            isDataPreloaded,
            hasError,
            validate,
            onInput,
        };
    },
});
</script>
